import "@/index.css";
import "@/App.css";
import React, { useState, useEffect } from "react";
import DashboardPage from "@/pages/DashboardPage";
import HomeDashboardPage from "@/pages/HomeDashboardPage";
import BusinessDashboardPage from "@/pages/BusinessDashboardPage";
import AccountsPage from "@/pages/AccountsPage";
import ImportPage from "@/pages/ImportPage";
import SectorsPage from "@/pages/SectorsPage";
import AlertsPage from "@/pages/AlertsPage";
import SyncPage from "@/pages/SyncPage";
import MappingsPage from "@/pages/MappingsPage";
import DecisionsPage from "@/pages/DecisionsPage";
import RulesPage from "@/pages/RulesPage";
import StockDetailPage from "@/pages/StockDetailPage";
import JournalPage from "@/pages/JournalPage";
import AssetsPage from "@/pages/AssetsPage";
import DividendsPage from "@/pages/DividendsPage";
import IdeasPage from "@/pages/IdeasPage";
import MomentumStrategyPage from "@/pages/MomentumStrategyPage";
import MomentumAnalysisPage from "@/pages/MomentumAnalysisPage";
import MomentumSleevePage from "@/pages/MomentumSleevePage";
import UsMomentumAnalysisPage from "@/pages/UsMomentumAnalysisPage";
import NetWorthPage from "@/pages/NetWorthPage";
import CryptoPage from "@/pages/CryptoPage";
import FundsIntelPage from "@/pages/FundsIntelPage";
import AdviserPage from "@/pages/AdviserPage";
import FinancialMemoryPage from "@/pages/FinancialMemoryPage";
import IncomePage from "@/pages/IncomePage";
import SpendingPage from "@/pages/SpendingPage";
import CashFlowPage from "@/pages/CashFlowPage";
import StatementsPage from "@/pages/StatementsPage";
import RewardsPage from "@/pages/RewardsPage";
import MoneyIntelligencePage from "@/pages/MoneyIntelligencePage";
import InvestmentPerformancePage from "@/pages/InvestmentPerformancePage";
import FinancialInputsPage from "@/pages/FinancialInputsPage";
import FamilyPlanningPage from "@/pages/FamilyPlanningPage";
import TaxHistoryPage from "@/pages/TaxHistoryPage";
import RelationshipsPage from "@/pages/RelationshipsPage";
import TravelPage from "@/pages/TravelPage";
import Layout from "@/components/Layout";
import AuthGate from "@/components/AuthGate";
import ErrorBoundary from "@/components/ErrorBoundary";
import OAuthCallbackHandler from "@/components/OAuthCallbackHandler";
import StockDetailDrawer from "@/components/StockDetailDrawer";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { pageFromSearch, urlForPage } from "@/lib/navigation";

// Paths that broker OAuth flows may redirect to. We render the callback
// handler for any of these, regardless of whether the user configured the
// broker dashboard with or without the `/api/` prefix.
const OAUTH_CALLBACK_PATHS = [
  "/api/broker/zerodha/callback",
  "/api/zerodha/callback",
  "/zerodha/callback",
  "/broker/zerodha/callback",
  "/breeze/callback",
  "/broker/breeze/callback",
  "/icici/callback",
];

function isOAuthCallback() {
  const p = window.location.pathname.toLowerCase();
  return OAUTH_CALLBACK_PATHS.some(cb => p === cb || p === cb + "/");
}

function MainApp() {
  const [page, setPage] = useState(() => pageFromSearch(window.location.search));
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedStockMarket, setSelectedStockMarket] = useState(null);
  const [drawerSymbol, setDrawerSymbol] = useState(null);
  const [drawerMarket, setDrawerMarket] = useState(null);

  const navigateToPage = (nextPage, { replace = false } = {}) => {
    const destination = pageFromSearch(`?page=${encodeURIComponent(nextPage)}`);
    setPage(destination);
    const nextUrl = urlForPage(destination);
    if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== nextUrl) {
      window.history[replace ? "replaceState" : "pushState"]({}, "", nextUrl);
    }
  };

  // Single click anywhere → open the side-drawer for a quick preview.
  const handleStockClick = (symbol, market = null) => {
    if (!symbol) return;
    setDrawerSymbol(symbol);
    setDrawerMarket(market);
  };

  // From inside the drawer, "Open Full Detail" routes to the full page.
  const handleOpenFullStock = (symbol, market = null) => {
    setDrawerSymbol(null);
    setSelectedStock(symbol);
    setSelectedStockMarket(market);
    setPage("stock-detail");
  };

  const handleBackFromStock = () => {
    setSelectedStock(null);
    setSelectedStockMarket(null);
    navigateToPage("dashboard");
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("breeze_connected")) { toast.success("ICICI Breeze connected!"); window.history.replaceState({}, "", "/"); }
    if (params.get("zerodha_connected")) { toast.success("Zerodha connected!"); window.history.replaceState({}, "", "/"); }
    if (params.get("error")) { toast.error(`Broker connection failed: ${params.get("error")}`); window.history.replaceState({}, "", "/"); }
  }, []);

  useEffect(() => {
    const restorePageFromUrl = () => {
      setPage(pageFromSearch(window.location.search));
      setSelectedStock(null);
      setSelectedStockMarket(null);
      setDrawerSymbol(null);
      setDrawerMarket(null);
    };
    window.addEventListener("popstate", restorePageFromUrl);
    return () => window.removeEventListener("popstate", restorePageFromUrl);
  }, []);

  const activePage = page === "stock-detail" ? "dashboard" : page;

  const pages = {
    home: <HomeDashboardPage onNavigate={navigateToPage} />,
    business: <BusinessDashboardPage onNavigate={navigateToPage} />,
    "stock-detail": <StockDetailPage symbol={selectedStock} market={selectedStockMarket} onBack={handleBackFromStock} />,
    dashboard: <DashboardPage onStockClick={handleStockClick} />,
    journal: <JournalPage onStockClick={handleStockClick} />,
    decisions: <DecisionsPage onStockClick={handleStockClick} />,
    adviser: <AdviserPage onStockClick={handleStockClick} />,
    planning: <FamilyPlanningPage initialView="goals" />,
    financial_memory: <FinancialMemoryPage />,
    family: <FamilyPlanningPage initialView="members" />,
    family_planning: <FamilyPlanningPage initialView="overview" />,
    family_planning_members: <FamilyPlanningPage initialView="members" />,
    family_planning_goals: <FamilyPlanningPage initialView="goals" />,
    family_planning_retirement: <FamilyPlanningPage initialView="retirement" />,
    family_planning_assumptions: <FamilyPlanningPage initialView="assumptions" />,
    tax_history: <TaxHistoryPage />,
    relationships: <RelationshipsPage />,
    travel: <TravelPage />,
    income: <MoneyIntelligencePage initialSection="Income" onNavigate={navigateToPage} />,
    money: <MoneyIntelligencePage initialSection="Overview" onNavigate={navigateToPage} />,
    spending: <MoneyIntelligencePage initialSection="Spending" onNavigate={navigateToPage} />,
    statements: <StatementsPage initialTab="Imports" />,
    transactions: <StatementsPage initialTab="Transactions" />,
    manual_entries: <FinancialInputsPage />,
    statement_imports: <StatementsPage initialTab="Imports" />,
    financial_statements: <StatementsPage initialTab="Financial Statements" />,
    investment_journey: <StatementsPage initialTab="Investments" />,
    investment_performance: <InvestmentPerformancePage />,
    rewards: <RewardsPage />,
    cashflow: <MoneyIntelligencePage initialSection="Cash Flow" onNavigate={navigateToPage} />,
    rules: <RulesPage />,
    accounts: <AccountsPage />, mappings: <MappingsPage />, import: <ImportPage />,
    sectors: <SectorsPage onStockClick={handleStockClick} />, alerts: <AlertsPage />, sync: <SyncPage />,
    debt_retirement: <AssetsPage initialTab="debt" section="debt-retirement" />,
    assets: <AssetsPage initialTab="real_estate" />,
    dividends: <DividendsPage onStockClick={handleStockClick} />,
    ideas: <IdeasPage onStockClick={handleStockClick} />,
    momentum_strategy: <MomentumStrategyPage onStockClick={handleStockClick} />,
    momentum_analysis: <MomentumAnalysisPage onStockClick={handleStockClick} />,
    momentum_sleeve: <MomentumSleevePage onNavigate={navigateToPage} />,
    us_momentum_analysis: <UsMomentumAnalysisPage onStockClick={handleStockClick} />,
    networth: <NetWorthPage />,
    crypto: <CryptoPage />,
    funds_intel: <FundsIntelPage />,
  };

  return (
    <ErrorBoundary>
      <AuthGate>
        <Layout activePage={activePage} onNavigate={(p) => { navigateToPage(p); setSelectedStock(null); setSelectedStockMarket(null); setDrawerSymbol(null); setDrawerMarket(null); }} onStockClick={handleStockClick}>
          <ErrorBoundary key={page}>
            {pages[page] || <DashboardPage onStockClick={handleStockClick} />}
          </ErrorBoundary>
        </Layout>
        <StockDetailDrawer
          symbol={drawerSymbol}
          market={drawerMarket}
          open={!!drawerSymbol}
          onClose={() => setDrawerSymbol(null)}
          onOpenFull={handleOpenFullStock}
        />
        <Toaster />
      </AuthGate>
    </ErrorBoundary>
  );
}

function App() {
  // Short-circuit to OAuth handler before rendering the regular app
  if (isOAuthCallback()) {
    return (
      <ErrorBoundary>
        <OAuthCallbackHandler />
        <Toaster />
      </ErrorBoundary>
    );
  }
  return <MainApp />;
}

export default App;
